import React from 'react';

import { Box } from '@mui/material';

import {
  StructuredTaskField,
  StructuredTaskFieldRow,
  StructuredTaskPanel,
  StructuredTaskSection,
} from '../../../common/components/StructuredTaskDetails';
import { TaskHeader } from '../../../common/components/TaskHeader';
import { DocumentType } from '../../../common/constants/documentType';
import { Resource } from '../../../common/constants/resource';
import { TaskState } from '../../../common/constants/states';
import ElementLocators from '../../../common/ElementLocators';
import { ITask, ITaskWithNotes } from '../../tasks/interfaces/ITask';
import { MIBSection } from '../../tasks/types/mib';
import { useSelector } from '../Detail';
import {
  calculateAgeFromDob,
  dateToString,
  genderShortFormToLongForm,
} from '../utilities/formattingUtilities';
import { MIBResponses } from './mib/MIBResponses';
import { SimpleUploadTaskWithNotesDetail } from './SimpleUploadTaskWithNotesDetail';

interface PlanFCodeDetail {
  reportCode?: string;
  newReportDate?: string;
  policyStatus?: string;
  reportResult?: string;
  insuredName?: string;
  insuredGender?: string;
  insuredDob?: string;
  birthState?: string;
  birthCountry?: string;
  occupation?: string;
  reportNote?: string;
  rawRowNumber?: number;
}

interface PlanFTaskData {
  csvFileName?: string;
  inquiryName?: string;
  inquiryGender?: string;
  inquiryDob?: string;
  codes?: PlanFCodeDetail[];
}

interface PlanFInquiryDetail {
  name?: string;
  gender?: string;
  dob?: string;
}

export const PlanFTaskDetail = ({
  task,
  uploadHandler,
  downloadHandler,
  taskStateHandler,
  deleteHandler,
}: {
  task: ITask;
  uploadHandler: (
    existingTaskData: ITask,
    data: FormData,
    type: string,
  ) => void;
  downloadHandler: (uri: string, fileName: string) => void;
  deleteHandler: (
    existingTaskData: ITask,
    documentId: number,
    reason: string,
  ) => void;
  taskStateHandler: (taskId: number, state: TaskState, reason?: string) => void;
}) => {
  const data = (task.taskData || {}) as PlanFTaskData;
  const codes = data.codes || [];
  const sections = groupCodesIntoReportSections(codes);

  const appName = useSelector(state => state.application.customerName);
  const appGender = useSelector(state => state.application.gender);
  const appBirthDate = useSelector(state => state.application.birthDate);

  const hasMibInquiry = Boolean(
    data.inquiryName || data.inquiryGender || data.inquiryDob,
  );
  const inquiry: PlanFInquiryDetail = hasMibInquiry
    ? {
        name: data.inquiryName,
        gender: data.inquiryGender,
        dob: data.inquiryDob,
      }
    : {
        name: appName || undefined,
        gender: appGender || undefined,
        dob: appBirthDate || undefined,
      };

  return (
    <Box data-label={ElementLocators.LOCATOR_PLAN_F_TASK}>
      <SimpleUploadTaskWithNotesDetail
        taskTitle={Resource.TASK_TITLE_PLAN_F}
        elementLocator={ElementLocators.LOCATOR_PLAN_F_TASK}
        task={task as ITaskWithNotes}
        uploadHandler={uploadHandler}
        documentType={DocumentType.PLAN_F}
        downloadHandler={downloadHandler}
        deleteHandler={deleteHandler}
        taskStateHandler={taskStateHandler}
      >
        {codes.length > 0 && (
          <Box mt={3} mb={4}>
            <StructuredTaskPanel>
              <TaskHeader title={Resource.TASK_TITLE_PLAN_F} />
              {renderPlanFApplicant(inquiry)}
              <MIBResponses sections={sections} />
            </StructuredTaskPanel>
          </Box>
        )}
      </SimpleUploadTaskWithNotesDetail>
    </Box>
  );
};

function renderPlanFApplicant(inquiry: PlanFInquiryDetail) {
  return (
    <StructuredTaskSection title={Resource.MIB_INQUIRY_DETAILS_SUBTITLE}>
      <StructuredTaskFieldRow>
        <StructuredTaskField
          label={Resource.MIB_APPLICANT_NAME}
          value={valueOrDash(inquiry.name)}
        />
        <StructuredTaskField
          label={Resource.MIB_APPLICANT_GENDER_AND_AGE}
          value={formatInquiryGenderAndAge(inquiry)}
        />
        <StructuredTaskField
          label={Resource.MIB_APPLICANT_BIRTH_DATE}
          value={dateToString(inquiry.dob)}
        />
      </StructuredTaskFieldRow>
    </StructuredTaskSection>
  );
}

function formatInquiryGenderAndAge(inquiry: PlanFInquiryDetail): string {
  const gender = inquiry.gender
    ? genderShortFormToLongForm(inquiry.gender) || inquiry.gender
    : Resource.NORA_M_DASH;
  const age = inquiry.dob
    ? calculateAgeFromDob(inquiry.dob)
    : Resource.NORA_M_DASH;

  return `${gender}, ${age}`;
}

function groupCodesIntoReportSections(codes: PlanFCodeDetail[]): MIBSection[] {
  const groupedSections = new Map<string, MIBSection>();

  codes.forEach(code => {
    // rawRowNumber is unique per CSV row; one row always maps to one report section.
    const key = String(code.rawRowNumber);
    const section = groupedSections.get(key);
    const mibCode = {
      date: code.newReportDate || '',
      code: valueOrDash(code.reportCode),
    };

    if (section) {
      section.codes.push(mibCode);
    } else {
      groupedSections.set(key, {
        detail: {
          result: formatPlanFResult(code),
          name: valueOrDash(code.insuredName),
          gender: formatGender(code.insuredGender),
          dob: code.insuredDob || '',
          birthState: code.birthState || Resource.NORA_M_DASH,
          birthCountry: code.birthCountry || Resource.NORA_M_DASH,
          occupation: valueOrDash(code.occupation),
        },
        codes: [mibCode],
        iai: [],
      });
    }
  });

  return [...groupedSections.values()];
}

function formatGender(gender?: string): string {
  return gender
    ? genderShortFormToLongForm(gender) || gender
    : Resource.NORA_M_DASH;
}

function formatPlanFResult(detail: PlanFCodeDetail): string {
  if (detail.reportResult && !/^\d+(\.\d+)?$/.test(detail.reportResult)) {
    return detail.reportResult;
  }
  return detail.reportResult !== undefined ? 'HIT' : Resource.NORA_M_DASH;
}

function valueOrDash(value?: string | number): string {
  return value !== undefined && value !== ''
    ? String(value)
    : Resource.NORA_M_DASH;
}
